<script lang="ts">
	import { getContext } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Text, Stack, SelectCard, Toggle, Row, Col, Alert } from '@Shelly/shelly-design-system';

	import { capFirstChar, conditionsFunc } from 'src/helpers/utilities';
	import { is_device_trigger_ui } from 'src/types/DeviceCondAndTriggerUI';
	import { scrollToElementInWizard } from 'src/components/Lists/list-helpers';
	import { WHEN_WIZARD_STEPS_INDEXES } from '../../helpers/when-wizard-helpers';
	import CustomDynamicInput from 'src/components/CustomDynamicInput.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { devices } from 'src/stores/lists/devices';
	import { isPresenceDevice } from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';

	export let sceneData: any;

	let additionalContentElRef: HTMLElement = null;
	let presenceWatchDefaultsDeviceId: string | undefined;
	let addConditionContainersRefs: SvelteStore<{
		wizardContainerRef: HTMLElement;
		scrollContainerRef: HTMLElement;
	}> = getContext('addConditionContainersRefs');

	const watchPropTypes = [
		{
			title: $format('condition_detailed_title', {
				default: 'Condition',
			}),
			desctiption: $format('condition_detailed_desc', {
				default:
					'Check if it is the desired device state, but does not activate scene by itself',
			}),
			value: 'condition',
		},
		{
			title: $format('trigger_detailed_title', {
				default: 'Trigger',
			}),
			desctiption: $format('trigger_detailed_desc', {
				default: 'Check if it is the desired device state and activate the scene',
			}),
			value: 'trigger',
		},
	];

	const triggerTypes = [
		{
			title: capFirstChar($format('condition_type_any', { default: 'On any change' })),
			description: $format('description_on_any_change', {
				default:
					'Triggers the scene every time when a monitored value (e.g., temperature or power) changes and meets the pre-set conditions.',
			}),
			value: 'any',
		},
		{
			title: capFirstChar($format('condition_type_repeat', { default: 'Repeatedly' })),
			description: $format('description_repeatedly', {
				default:
					'Checks the condition every minute and triggers actions if the condition state remains unchanged.',
			}),
			value: 'repeat',
		},
		{
			title: capFirstChar($format('condition_type_one', { default: 'Once' })),
			description: $format('description_once', {
				default:
					'Activates the scene once when the condition is met and requires the condition to be repeated for reactivation.',
			}),
			value: 'once',
		},
	];

	let showTriggerTypeSettings = false;

	if (is_device_trigger_ui(sceneData.translated)) {
		sceneData.watchProp = { ...sceneData.translated.cond_type };
	}

	if (sceneData.translated && !sceneData.translated.cond_type) {
		sceneData.selectedWatchType = 'condition';
	}

	$: {
		const selId = sceneData?.selectedDevice?.id as string | undefined;
		if (!selId || !isPresenceDevice(selId, $devices)) {
			presenceWatchDefaultsDeviceId = undefined;
		} else if (presenceWatchDefaultsDeviceId !== selId) {
			presenceWatchDefaultsDeviceId = selId;
			const isEdit = sceneData.when_info?.mode === 'edit';
			const keepSavedTrigger =
				isEdit && is_device_trigger_ui(sceneData.translated) && sceneData.translated?.cond_type;
			if (!keepSavedTrigger) {
				sceneData.selectedWatchType = 'trigger';
				sceneData.watchProp = {
					cond_type_id: sceneData.watchProp?.cond_type_id ?? 1,
					cond_type_name: 'any',
					valid_for_min: 0,
				};
			}
		}
	}

	$: showPresenceTriggerAnyHint =
		isPresenceDevice(sceneData?.selectedDevice?.id, $devices) &&
		sceneData.selectedWatchType === 'trigger' &&
		sceneData?.watchProp?.cond_type_name === 'any';

	let error;
	$: {
		if (
			error &&
			(!showTriggerTypeSettings ||
				sceneData.selectedWatchType !== 'trigger' ||
				sceneData?.watchProp?.cond_type_name !== 'once')
		) {
			sceneData.watchProp.valid_for_min = 0;
			sceneData.isValid = true;
		}

		error = conditionsFunc(sceneData.watchProp.valid_for_min)
			? $format('value_must_be_equal_or_greater_than_zero', {
					default: 'Value must be an integer and equal or greater than 0.',
				})
			: undefined;
	}

	$: isTriggerType = sceneData.selectedWatchType === 'trigger';
	$: isOnceTriggerType = sceneData?.watchProp?.cond_type_name === 'once';

	$: if (additionalContentElRef) {
		scrollToElementInWizard(
			$addConditionContainersRefs.wizardContainerRef,
			additionalContentElRef,
			$addConditionContainersRefs.scrollContainerRef,
		);
	}
	let inputHandler = (e) => {
		sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.WATH_PROP_AS_STEP;
		if (conditionsFunc(e.target.value)) {
			sceneData.isValid = false;
		} else {
			sceneData.isValid = true;
		}
		sceneData.watchProp.valid_for_min = e.target.value;
	};

	let minusFunction = () => {
		if (!error && sceneData.watchProp.valid_for_min > 0) {
			sceneData.watchProp.valid_for_min = +sceneData.watchProp.valid_for_min - 1;
		} else {
			sceneData.watchProp.valid_for_min = 0;
		}
		sceneData.isValid = true;
	};

	let plusFunction = () => {
		if (!error) {
			sceneData.watchProp.valid_for_min = +sceneData.watchProp.valid_for_min + 1;
		} else {
			sceneData.watchProp.valid_for_min = 0;
		}
		sceneData.isValid = true;
	};
</script>

<Text styleType="h2" class="mb-s">
	{$format('label_watch_as', { default: 'Watch the property as:' })}
</Text>

<!-- // TODO ADD NEW TRANSLATIONS -->
<Stack gap="s" direction="row">
	{#each watchPropTypes as watchType}
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			variant="radio"
			type="horizontal"
			class="width-full"
			selected={sceneData.selectedWatchType === watchType.value}
			on:click={() => {
				sceneData.selectedWatchType = watchType.value;
			}}
			title={watchType.title}
			descriptiveText={watchType.desctiption}
		/>
	{/each}
</Stack>

<Toggle
	bind:checked={showTriggerTypeSettings}
	size="m"
	class="mt-m"
	disabled={!isTriggerType}
	id="toggle-hcct7b6"
>
	{$format('trigger_custom_settings', {
		default: 'Trigger custom settings',
	})}
</Toggle>

{#if showTriggerTypeSettings && isTriggerType}
	{#if showPresenceTriggerAnyHint}
		<Alert
			type="info"
			class="mt-m mb-none"
			heading={$format('presence_scene_watch_trigger_any_hint', {
				default:
					'Presence and zone values update when the sensor reports a change. “On any change” is recommended and pre-selected so the scene runs whenever the monitored value updates.',
			})}
		/>
	{/if}
	<div bind:this={additionalContentElRef}>
		<Text styleType="body-small" class="mb-s mt-s">
			{capFirstChar($format('condition_type_title'))}
		</Text>

		<Row>
			{#each triggerTypes as trigger}
				<Col md={6} xs={12}>
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						class="width-full"
						variant="radio"
						type="horizontal"
						title={trigger.title}
						descriptiveText={trigger.description}
						selected={trigger.value === sceneData?.watchProp?.cond_type_name}
						on:click={() => {
							sceneData.watchProp.cond_type_name = trigger.value;
						}}
						size="m"
					/>
				</Col>
			{/each}
		</Row>

		<Text styleType="body-small" class="mt-s mb-s">
			{capFirstChar(
				$format('condition_type_valid_minutes', {
					values: {
						min: error ? 0 : sceneData.watchProp.valid_for_min,
					},
					default: `Condition valid ${sceneData.watchProp.valid_for_min} min(s)`,
				}),
			)}
		</Text>

		<CustomDynamicInput
			additionalInputAttributes={{
				min: 0,
				icon: 'fas fa-clock',
				disabled: !isOnceTriggerType,
			}}
			additionalButtonAttributes={{
				disabled: !isOnceTriggerType,
			}}
			disabledMinusBtn={+sceneData.watchProp.valid_for_min <= 0}
			bind:value={sceneData.watchProp.valid_for_min}
			bind:error
			bind:inputHandler
			bind:minusFunction
			bind:plusFunction
		/>
	</div>
{/if}
